import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDiaryContext } from "../../../../../context/DiaryContext";
import { Clock } from "../../../../../icons";
import { cn } from "../../../../../lib/utils";
import {
  TOUCH_DRAG_DROP_EVENT,
  TOUCH_DRAG_HOVER_EVENT
} from "../../../../../utils/dragCloneUtils";
import {
  FloorplanBooking,
  FloorplanConfig,
  RenderableTable
} from "../floorplanTypes";
import {
  getBookingDurationPercentage,
  getRotatedBoundingBox,
  getStageColour,
  getStageInitial
} from "../floorplanUtils";
import BookingCard from "./BookingCard";
import { DecorLabel } from "./DecorLabel";
import { FloatingBadge } from "./FloatingBadge";
import { BanquetteTable } from "./shapes/BanquetteTable";
import { CommunalTable } from "./shapes/CommunalTable";

const clearTimeoutRef = (
  ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
): void => {
  if (ref.current) {
    clearTimeout(ref.current);
    ref.current = null;
  }
};

// Module-level shared timeout: ensures entering one table cancels any
// pending hover-leave timeout from a previously hovered table, preventing
// stale onHoverTable(null) calls from overriding the new table's hover.
let sharedHoverLeaveTimeout: ReturnType<typeof setTimeout> | null = null;

const clearSharedHoverLeaveTimeout = (): void => {
  if (sharedHoverLeaveTimeout !== null) {
    clearTimeout(sharedHoverLeaveTimeout);
    sharedHoverLeaveTimeout = null;
  }
};

const getTableBorderRadius = (type: string, isWall: boolean): string => {
  if (type === "Circle" || type === "Oval" || type === "BarStool")
    return "rounded-full";
  if (type === "HighTop") return "rounded-lg";
  if (isWall) return "rounded-none";
  return "rounded-2xl";
};

const getTableBorderRadiusValue = (type: string, isWall: boolean): string => {
  if (type === "Circle" || type === "Oval" || type === "BarStool")
    return "9999px";
  if (type === "HighTop") return "0.5rem";
  if (isWall) return "0px";
  return "1rem";
};

const getBackgroundColour = (
  isAnySelected: boolean,
  isSelected: boolean,
  isDecor: boolean,
  isDark: boolean,
  tableColour: string
): string => {
  if (isAnySelected && !isSelected) {
    return isDark ? "rgba(15,23,42,0.7)" : "rgba(148,163,184,0.3)";
  }
  if (isDecor) {
    return isDark ? "#1e293b" : "#f8fafc";
  }
  return tableColour;
};

const getClosedColour = (isDark: boolean): string =>
  isDark ? "bg-red-500/10 text-red-500" : "bg-red-100 text-red-700";

const getTableOpacity = (
  isAnySelected: boolean,
  isSelected: boolean
): number => (isAnySelected && !isSelected ? 0.75 : 1);

const getTableShadow = (isSelected: boolean): string | undefined =>
  isSelected ? "0 0 16px 4px rgba(99,102,241,0.5)" : undefined;

interface TableViewProps {
  table: RenderableTable;
  isSelected: boolean;
  isAnySelected: boolean;
  isLastUpdated: boolean;
  shouldShowTableNameInBadge?: boolean;
  isDark?: boolean;
  config: FloorplanConfig;
  isDragging?: boolean;
  isPanning?: boolean;
  isHovered?: boolean;
  onSelectTable: (configId: string | null) => void;
  onHoverTable: (configId: string | null) => void;
  onDropBooking?: (
    areaId: string,
    areaName: string,
    bookingData: string
  ) => void;
  onRequestAddBooking?: (area: RenderableTable["area"]) => void;
}

const TableView: React.FC<TableViewProps> = ({
  table,
  isSelected,
  isAnySelected,
  isLastUpdated,
  shouldShowTableNameInBadge = false,
  isDark = false,
  config,
  isDragging = false,
  isPanning = false,
  isHovered = false,
  onSelectTable,
  onHoverTable,
  onDropBooking,
  onRequestAddBooking
}) => {
  const { stages } = useDiaryContext();
  const [activeIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const tableElementRef = useRef<HTMLDivElement | HTMLButtonElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const badgeDismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const isDecor = !table.area;
  const isWall = table.decorType === "wall";
  const hasBookings = table.bookings.length > 0;
  const isClosed = table.state === "closed";
  const currentBooking = table.bookings[activeIndex];
  const isCurrentBookingActive =
    !!currentBooking &&
    new Date(currentBooking.startTime).getTime() <= Date.now();
  const showClosedText = isClosed && !isCurrentBookingActive;

  const nextBooking = useMemo(() => {
    if (!hasBookings) return undefined;
    return [...table.bookings]
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      )
      .find(
        (b) =>
          new Date(b.startTime).getTime() >
          new Date(currentBooking?.endTime ?? 0).getTime()
      );
  }, [hasBookings, table.bookings, currentBooking?.endTime]);

  const isSmall = table.width < 80;
  const isTiny = table.width < 50;

  const { offsetX, offsetY, boundingWidth, boundingHeight } = useMemo(
    () => getRotatedBoundingBox(table.width, table.height, table.rotation),
    [table.width, table.height, table.rotation]
  );

  const handleMouseEnter = useCallback((): void => {
    if (isDecor) return;
    clearSharedHoverLeaveTimeout();
    clearTimeoutRef(hoverTimeoutRef);
    clearTimeoutRef(badgeDismissTimeoutRef);
    setIsFadingOut(false);
    onHoverTable(table.configId);
    setShowBadge(true);
  }, [isDecor, table.configId, onHoverTable]);

  const handleMouseLeave = useCallback((): void => {
    const timeoutId = setTimeout(() => {
      sharedHoverLeaveTimeout = null;
      onHoverTable(null);
      setIsFadingOut(true);
      setShowBadge(false);
      badgeDismissTimeoutRef.current = setTimeout(() => {
        setIsFadingOut(false);
      }, 200);
    }, 200);
    hoverTimeoutRef.current = timeoutId;
    sharedHoverLeaveTimeout = timeoutId;
  }, [onHoverTable]);

  const handleFocus = useCallback((): void => {
    if (isDecor) return;
    clearSharedHoverLeaveTimeout();
    clearTimeoutRef(hoverTimeoutRef);
    clearTimeoutRef(badgeDismissTimeoutRef);
    setIsFadingOut(false);
    onHoverTable(table.configId);
    setShowBadge(true);
  }, [isDecor, table.configId, onHoverTable]);

  const handleBlur = useCallback((): void => {
    onHoverTable(null);
    setIsFadingOut(true);
    setShowBadge(false);
    badgeDismissTimeoutRef.current = setTimeout(() => {
      setIsFadingOut(false);
    }, 200);
  }, [onHoverTable]);

  const handleBadgeMouseEnter = useCallback((): void => {
    clearSharedHoverLeaveTimeout();
    clearTimeoutRef(hoverTimeoutRef);
    clearTimeoutRef(badgeDismissTimeoutRef);
    onHoverTable(table.configId);
    setShowBadge(true);
  }, [onHoverTable, table.configId]);

  const handleBadgeMouseLeave = useCallback((): void => {
    clearTimeoutRef(hoverTimeoutRef);
    clearTimeoutRef(badgeDismissTimeoutRef);
    onHoverTable(null);
    setIsFadingOut(true);
    setShowBadge(false);
    badgeDismissTimeoutRef.current = setTimeout(() => {
      setIsFadingOut(false);
    }, 200);
  }, [onHoverTable]);

  useEffect(() => {
    return () => {
      clearTimeoutRef(hoverTimeoutRef);
      clearTimeoutRef(badgeDismissTimeoutRef);
    };
  }, []);

  useEffect(() => {
    const element = tableElementRef.current;
    if (!element) {
      return;
    }

    const handleTouchDrop = (event: Event) => {
      const customEvent = event as CustomEvent<{ bookingData: string }>;
      if (!table.area || !onDropBooking || !customEvent.detail?.bookingData) {
        return;
      }

      onDropBooking(
        table.area.id,
        table.name ?? "",
        customEvent.detail.bookingData
      );
      setIsDragOver(false);
    };

    element.addEventListener(
      TOUCH_DRAG_DROP_EVENT,
      handleTouchDrop as EventListener
    );

    return () => {
      element.removeEventListener(
        TOUCH_DRAG_DROP_EVENT,
        handleTouchDrop as EventListener
      );
    };
  }, [table.area, table.name, onDropBooking]);

  useEffect(() => {
    const handleTouchHover = (event: Event) => {
      const customEvent = event as CustomEvent<{ tableId: string | null }>;
      setIsDragOver(customEvent.detail?.tableId === table.configId);
    };

    window.addEventListener(
      TOUCH_DRAG_HOVER_EVENT,
      handleTouchHover as EventListener
    );

    return () => {
      window.removeEventListener(
        TOUCH_DRAG_HOVER_EVENT,
        handleTouchHover as EventListener
      );
    };
  }, [table.configId]);

  useEffect(() => {
    setShowBadge(isHovered);
  }, [isHovered]);

  const borderRadius = useMemo(
    () => getTableBorderRadius(table.type, isWall),
    [table.type, isWall]
  );

  const borderRadiusValue = useMemo(
    () => getTableBorderRadiusValue(table.type, isWall),
    [table.type, isWall]
  );

  const progressPercent = currentBooking
    ? getBookingDurationPercentage(currentBooking)
    : 0;

  const stageColour = useMemo(
    () =>
      currentBooking
        ? getStageColour(stages, currentBooking.currentStage)
        : "transparent",
    [stages, currentBooking]
  );

  const borderStyles = useMemo((): string => {
    const themed = (dark: string, light: string): string =>
      isDark ? dark : light;
    const isHighlighted = isHovered || showBadge;
    const hasStageRing =
      hasBookings && !!currentBooking?.currentStage && !isHighlighted;
    const isAvailableForDrop = isDragging && !hasBookings && !isClosed;

    if (isSelected) return "2px solid #6366f1";
    if (isDragOver) return "2px dashed #6366f1";
    if (isDecor) return themed("2px solid #334155", "2px solid #cbd5e1");
    if (isAnySelected)
      return themed(
        "2px solid rgba(255,255,255,0.05)",
        "2px solid rgba(0,0,0,0.05)"
      );
    if (isHighlighted) return themed("2px solid #60a5fa", "2px solid #3b82f6");
    if (isClosed) return themed("2px solid #991b1b", "2px solid #fca5a5");
    if (hasStageRing) return "2px solid rgba(255,255,255,0.1)";
    if (hasBookings) return themed("2px solid #3730a3", "2px solid #c7d2fe");
    if (isAvailableForDrop)
      return themed("2px solid #10b981", "2px solid #34d399");
    return themed("2px solid rgba(255,255,255,0.1)", "2px solid #e2e8f0");
  }, [
    isSelected,
    isAnySelected,
    isDragOver,
    isDecor,
    isDark,
    isHovered,
    showBadge,
    isClosed,
    hasBookings,
    currentBooking?.currentStage,
    isDragging
  ]);

  const backgroundColour = useMemo(
    () =>
      getBackgroundColour(
        isAnySelected,
        isSelected,
        isDecor,
        isDark,
        table.colour
      ),
    [isAnySelected, isSelected, isDecor, isDark, table.colour]
  );

  const renderDecorativeContent = useCallback((): React.ReactNode => {
    if (!isDecor) return null;

    return (
      <DecorLabel name={table.name} rotation={table.rotation} isDark={isDark} />
    );
  }, [isDecor, table.name, table.rotation, isDark]);

  const renderTinyBooking = useCallback(
    (booking: FloorplanBooking): React.ReactNode => (
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
        style={{
          transform: `rotate(${-table.rotation}deg)`,
          borderRadius: borderRadiusValue
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            borderRadius: borderRadiusValue
          }}
        />
        <span className="font-black uppercase tracking-tight leading-none text-[12px] text-black relative z-10">
          {getStageInitial(booking.currentStage) ? (
            getStageInitial(booking.currentStage)
          ) : (
            <Clock size={16} style={{ color: "black" }} />
          )}
        </span>
      </div>
    ),
    [table.rotation, borderRadiusValue]
  );

  const freeTextColour = useMemo((): string => {
    if (isDragging) return isDark ? "text-emerald-200" : "text-emerald-700";
    return isDark ? "text-emerald-400" : "text-emerald-700";
  }, [isDragging, isDark]);

  const freeBadgeClasses = useMemo((): string => {
    if (isDragging) {
      return isDark
        ? "bg-emerald-500/40 text-emerald-200 border-emerald-400 shadow-lg shadow-emerald-500/30"
        : "bg-emerald-50 text-emerald-700 border-emerald-200";
    }
    return isDark
      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
  }, [isDragging, isDark]);

  const closedSizeClasses = useMemo((): string => {
    if (isTiny) return "text-[7px] px-1 py-0.5";
    if (isSmall) return "text-[9px] px-1.5 py-0.5";
    return "text-[11px] px-3 py-1";
  }, [isTiny, isSmall]);

  const zIndexClass = useMemo((): string => {
    if (isSelected) return "z-50 shadow-2xl scale-[1.01]";
    if (isDecor) return "z-10";
    return "z-30";
  }, [isSelected, isDecor]);

  const renderFreeState = useCallback(
    (): React.ReactNode => (
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
        style={{ transform: `rotate(${-table.rotation}deg)` }}
      >
        {isTiny ? (
          <span
            className={`font-black uppercase tracking-tight leading-none text-[12px] ${freeTextColour}`}
          >
            F
          </span>
        ) : (
          <div
            className={`font-black rounded border uppercase tracking-tight leading-none ${
              isSmall ? "text-[9px] px-1.5 py-0.5" : "text-[11px] px-3 py-1"
            } ${freeBadgeClasses}`}
          >
            FREE
          </div>
        )}
      </div>
    ),
    [table.rotation, isTiny, isSmall, freeTextColour, freeBadgeClasses]
  );

  const tableStatusLabel = useMemo((): string => {
    if (hasBookings) return "occupied";
    if (isClosed) return "closed";
    return "free";
  }, [hasBookings, isClosed]);

  const tableNameTitle = useMemo((): string => {
    const seats = table.area?.max_people;
    return seats ? `${table.name} (${seats} seats)` : table.name;
  }, [table.name, table.area?.max_people]);

  const tableNameDisplay = useMemo((): string => {
    const seats = table.area?.max_people;
    return seats ? `${table.name} (0/${seats})` : table.name;
  }, [table.name, table.area?.max_people]);

  const animateClass = isLastUpdated ? "animate-blink" : "";
  const cursorClass = isDecor ? "cursor-default" : "cursor-pointer";
  const tableOpacity = getTableOpacity(isAnySelected, isSelected);
  const tableShadow = getTableShadow(isSelected);
  const isHighlighted = isHovered || showBadge;
  const showStageRing =
    hasBookings &&
    !!currentBooking?.currentStage &&
    !isSelected &&
    !isDragOver &&
    !isHighlighted;

  const handleClick = useCallback(
    (e: React.MouseEvent): void => {
      if (isPanning) return;
      e.stopPropagation();
      e.preventDefault();
      if (!isDecor) {
        onSelectTable(isSelected ? null : table.configId);
      }
    },
    [isPanning, isDecor, isSelected, table.configId, onSelectTable]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if ((e.key === "Enter" || e.key === " ") && !isDecor) {
        e.preventDefault();
        onSelectTable(isSelected ? null : table.configId);
      }
    },
    [isDecor, isSelected, table.configId, onSelectTable]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent): void => {
      if (isPanning) return;
      if (isDragging) return;
      if (!table.area) return;
      if (hasBookings) return;
      if (isClosed) return;
      e.stopPropagation();
      e.preventDefault();
      onRequestAddBooking?.(table.area);
    },
    [
      isPanning,
      isDragging,
      table.area,
      hasBookings,
      isClosed,
      onRequestAddBooking
    ]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent): void => {
      if (isDecor) return;
      e.preventDefault();
      setIsDragOver(true);
    },
    [isDecor]
  );

  const handleDragLeave = useCallback((): void => {
    setIsDragOver(false);
  }, []);

  // The "booking" data transfer key is consistent with both DiaryBookingCard
  // (useDiaryBookingCardHandlers.ts) and RunSheetCard which both call
  // e.dataTransfer.setData("booking", JSON.stringify(booking)).
  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      setIsDragOver(false);
      if (isDecor) return;
      e.preventDefault();
      const bookingData = e.dataTransfer.getData("booking");
      if (bookingData && table.area && onDropBooking) {
        onDropBooking(table.area.id, table.name ?? "", bookingData);
      }
    },
    [isDecor, table.area, table.name, onDropBooking]
  );

  const closedColour = getClosedColour(isDark);

  const renderClosedContent = useCallback(
    (): React.ReactNode => (
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
        style={{ transform: `rotate(${-table.rotation}deg)` }}
      >
        <div
          className={`font-black rounded uppercase tracking-tight leading-none ${closedSizeClasses} ${closedColour}`}
        >
          CLOSED
        </div>
      </div>
    ),
    [table.rotation, closedSizeClasses, closedColour]
  );

  const renderBookingByType = useCallback((): React.ReactNode => {
    if (table.type === "Banquette") {
      return isTiny && currentBooking ? (
        renderTinyBooking(currentBooking)
      ) : (
        <BanquetteTable
          booking={currentBooking}
          width={table.width}
          colour={table.colour}
          rotation={table.rotation}
          isDark={isDark}
        />
      );
    }

    if (table.type === "CommunalTable") {
      return isTiny && currentBooking ? (
        renderTinyBooking(currentBooking)
      ) : (
        <CommunalTable
          bookings={table.bookings}
          width={table.width}
          colour={table.colour}
          rotation={table.rotation}
          isDark={isDark}
        />
      );
    }

    if (!currentBooking) return null;
    return isTiny ? (
      renderTinyBooking(currentBooking)
    ) : (
      <BookingCard
        booking={currentBooking}
        rotation={table.rotation}
        shape={table.type}
        tableWidth={table.width}
      />
    );
  }, [
    isTiny,
    isDark,
    currentBooking,
    table.type,
    table.bookings,
    table.width,
    table.colour,
    table.rotation,
    renderTinyBooking
  ]);

  const renderBookingContent = useCallback((): React.ReactNode => {
    if (isDecor) return renderDecorativeContent();
    if (showClosedText) return renderClosedContent();
    if (!hasBookings && !isClosed) return renderFreeState();
    if (!hasBookings) return null;
    return renderBookingByType();
  }, [
    isDecor,
    showClosedText,
    hasBookings,
    isClosed,
    renderDecorativeContent,
    renderClosedContent,
    renderFreeState,
    renderBookingByType
  ]);

  const showTableName =
    !isDecor && config.displayTableNames && !shouldShowTableNameInBadge;
  const showFloatingBadge =
    !isDecor &&
    (showBadge || isFadingOut) &&
    (hasBookings || shouldShowTableNameInBadge || isClosed);
  const tableNameLabelColour = isDark ? "text-white/60" : "text-slate-500";

  const tableClassName = cn(
    `absolute transition-all duration-300 transform-gpu ${zIndexClass} ${animateClass} ${borderRadius} ${cursorClass} select-none`
  );

  const tableStyle: React.CSSProperties = {
    left: table.x,
    top: table.y,
    width: table.width,
    height: table.height,
    backgroundColor: backgroundColour,
    transformOrigin: "0 0",
    transform: `rotate(${table.rotation}deg)`,
    border: borderStyles,
    boxShadow: tableShadow,
    opacity: tableOpacity
  };

  const stageRingContent = showStageRing ? (
    <div
      className="absolute -inset-[2px] rounded-[inherit] pointer-events-none"
      style={
        {
          background: `conic-gradient(from -90deg, ${stageColour} ${progressPercent}%, transparent ${progressPercent}%)`,
          padding: "2px",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          maskComposite: "exclude"
        } as React.CSSProperties
      }
    />
  ) : null;

  const tableElement = isDecor ? (
    <div
      ref={tableElementRef as React.RefObject<HTMLDivElement>}
      id={`item-${table.configId}`}
      className={tableClassName}
      data-table-id={table.configId}
      style={tableStyle}
    >
      {stageRingContent}
      {renderBookingContent()}
    </div>
  ) : (
    <button
      type="button"
      ref={tableElementRef as React.RefObject<HTMLButtonElement>}
      id={`item-${table.configId}`}
      className={tableClassName}
      data-table-id={table.configId}
      style={tableStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={`Table ${table.name}, ${tableStatusLabel}`}
      aria-describedby={
        showFloatingBadge ? `tooltip-${table.configId}` : undefined
      }
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {stageRingContent}
      {renderBookingContent()}
    </button>
  );

  return (
    <>
      {tableElement}

      {showTableName && (
        <div
          className={`absolute whitespace-nowrap text-[12px] font-bold tracking-tight pointer-events-none text-center truncate max-w-[120px] ${tableNameLabelColour}`}
          style={{
            left: table.x - offsetX + boundingWidth / 2,
            top: table.y - offsetY - 20,
            transform: "translateX(-50%)"
          }}
          title={tableNameTitle}
        >
          {tableNameDisplay}
        </div>
      )}

      {showFloatingBadge && (
        <FloatingBadge
          booking={currentBooking ?? undefined}
          nextBooking={nextBooking}
          tableName={table.name}
          tableWidth={boundingWidth}
          tableHeight={boundingHeight}
          tableX={table.x - offsetX}
          tableY={table.y - offsetY}
          position="bottom"
          isVisible={showBadge}
          showTableNameInBadge={shouldShowTableNameInBadge}
          onMouseEnter={handleBadgeMouseEnter}
          onMouseLeave={handleBadgeMouseLeave}
          isDark={isDark}
          isClosed={isClosed}
          closureInfo={table.closureInfo}
          tooltipId={`tooltip-${table.configId}`}
        />
      )}
    </>
  );
};

export default memo(TableView);
